import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { NAV_LINKS } from "@/data";
import { Logo } from "@/components/site/Logo";

export const Navbar = ({ onBookDemo }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (href) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const go = (href) => {
    setOpen(false);
    if (href.startsWith("/")) {
      navigate(href);
      window.scrollTo({ top: 0 });
      return;
    }
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollTo(href), 350);
    } else {
      scrollTo(href);
    }
  };

  const goHome = () => {
    setOpen(false);
    if (location.pathname !== "/") navigate("/");
    else window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const bookDemo = () => {
    setOpen(false);
    if (onBookDemo) onBookDemo();
    else navigate("/");
  };

  return (
    <motion.header
      data-testid="site-navbar"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        scrolled ? "border-b border-slate-200 bg-white/80 backdrop-blur-xl" : "border-b border-transparent"
      }`}
    >
      <nav className="mx-auto flex max-w-[1400px] items-center justify-between px-6 py-4 lg:px-10">
        <button data-testid="nav-logo" onClick={goHome} className="cursor-pointer">
          <Logo />
        </button>

        <div className="hidden items-center gap-9 lg:flex">
          {NAV_LINKS.map((l) => (
            <button
              key={l.label}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              onClick={() => go(l.href)}
              className="font-mono-p text-xs uppercase tracking-[0.18em] text-slate-500 transition-colors hover:text-slate-900"
            >
              {l.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <button
            data-testid="nav-book-demo"
            onClick={bookDemo}
            className="group hidden items-center gap-2 bg-volt px-5 py-2.5 font-display text-sm font-bold text-white transition-transform hover:-translate-y-0.5 sm:flex"
          >
            Book a Demo
            <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2.2} />
          </button>
          <button data-testid="nav-mobile-toggle" onClick={() => setOpen((o) => !o)} className="lg:hidden" aria-label="Menu">
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden border-t border-slate-200 bg-white/95 backdrop-blur-xl lg:hidden"
          >
            <div className="flex flex-col gap-1 px-6 py-4">
              {NAV_LINKS.map((l) => (
                <button
                  key={l.label}
                  data-testid={`nav-mobile-${l.label.toLowerCase()}`}
                  onClick={() => go(l.href)}
                  className="py-3 text-left font-display text-lg font-bold text-slate-700"
                >
                  {l.label}
                </button>
              ))}
              <button data-testid="nav-mobile-book-demo" onClick={bookDemo} className="mt-2 bg-volt px-5 py-3 text-center font-display font-bold text-white">
                Book a Demo
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
